import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { MailCheck } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';
import { notify } from '../utils/notifications';

const RESEND_COOLDOWN_S = 60;
const POLL_INTERVAL_MS = 5000;

export default function VerifyEmailScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verificationToken = searchParams.get('token');
  const {
    authUser,
    authSession,
    isAuthSessionCurrent,
    signOut,
  } = useApp();
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);
  const [linkVerified, setLinkVerified] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const verifiedRef = useRef(false);

  // If a ?token= param is present (user clicked the link), verify immediately
  useEffect(() => {
    const token = verificationToken;
    const session = authSession;
    if (!token || verifiedRef.current) return;
    let disposed = false;
    verifiedRef.current = true;
    void (async () => {
      try {
        const isCurrentSession = () => !!session && isAuthSessionCurrent(session);
        const verifyOptions = isCurrentSession() && session
          ? { signal: session.signal }
          : {};
        await authService.verifyEmail(
          token,
          verifyOptions,
        );
        if (disposed) return;
        if (session && isAuthSessionCurrent(session)) {
          await signOut();
          if (disposed) return;
        }
        notify.success('Email verified!', 'Your account is now active.');
        setLinkVerified(true);
      } catch {
        if (disposed) return;
        notify.error('Verification failed', 'The link may have expired. Request a new one below.');
      }
    })();
    return () => { disposed = true; };
  }, [
    authSession,
    isAuthSessionCurrent,
    navigate,
    signOut,
    verificationToken,
  ]);

  // Poll /users/me every 5 s — navigate away once verified
  useEffect(() => {
    if (verificationToken) return;
    const session = authSession;
    if (!session || !isAuthSessionCurrent(session)) return;
    let disposed = false;
    pollRef.current = setInterval(async () => {
      try {
        const user = await authService.getCurrentUser({
          signal: session.signal,
        });
        if (disposed || !isAuthSessionCurrent(session)) return;
        if (user?.is_verified) {
          clearInterval(pollRef.current!);
          navigate('/complete-profile', { replace: true });
        }
      } catch {
        // network error — silently retry
      }
    }, POLL_INTERVAL_MS);
    return () => {
      disposed = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [authSession, isAuthSessionCurrent, navigate, verificationToken]);

  // Cooldown countdown
  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(timerRef.current!); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current!);
  }, [cooldown]);

  const canResend = !!authUser?.email && !!authSession && isAuthSessionCurrent(authSession);

  async function handleResend() {
    const session = authSession;
    if (
      cooldown > 0 ||
      !session ||
      !authUser?.email ||
      !isAuthSessionCurrent(session)
    ) {
      navigate('/signin', { replace: true });
      return;
    }
    const email = authUser.email;
    setSending(true);
    try {
      await authService.requestVerifyEmail(
        email,
        { signal: session.signal },
      );
      if (!isAuthSessionCurrent(session)) return;
      notify.success('Email sent', 'Check your inbox for the verification link.');
      setCooldown(RESEND_COOLDOWN_S);
    } catch {
      if (isAuthSessionCurrent(session)) {
        notify.error('Could not send email', 'Please try again in a moment.');
      }
    } finally {
      if (isAuthSessionCurrent(session)) setSending(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate('/signin', { replace: true });
  }

  if (linkVerified) {
    return (
      <div
        className="size-full flex flex-col items-center justify-center px-6"
        style={{ backgroundColor: 'var(--app-bg)' }}
      >
        <div
          className="rounded-2xl size-20 flex items-center justify-center mb-8 border"
          style={{ backgroundColor: 'var(--app-accent-dim)', borderColor: 'var(--app-accent-border)' }}
        >
          <MailCheck className="w-10 h-10" style={{ color: 'var(--app-accent)' }} />
        </div>

        <h1
          className="text-[28px] font-semibold tracking-tight text-center mb-3"
          style={{ color: 'var(--app-text)' }}
        >
          Email verified
        </h1>
        <p className="text-sm text-center mb-10" style={{ color: 'var(--app-text-subtle)' }}>
          Your account is ready. You can now sign in with your password on your phone.
        </p>
        <button
          onClick={() => navigate('/signin', { replace: true })}
          className="h-12 w-full rounded-xl text-base font-medium active:opacity-80 transition-opacity"
          style={{ backgroundColor: 'var(--app-accent-solid)', color: 'white' }}
        >
          Continue to sign in
        </button>
      </div>
    );
  }

  return (
    <div
      className="size-full flex flex-col items-center justify-center px-6"
      style={{ backgroundColor: 'var(--app-bg)' }}
    >
      <div
        className="rounded-2xl size-20 flex items-center justify-center mb-8 border"
        style={{ backgroundColor: 'var(--app-accent-dim)', borderColor: 'var(--app-accent-border)' }}
      >
        <MailCheck className="w-10 h-10" style={{ color: 'var(--app-accent)' }} />
      </div>

      <h1
        className="text-[28px] font-semibold tracking-tight text-center mb-3"
        style={{ color: 'var(--app-text)' }}
      >
        Check your email
      </h1>
      <p className="text-base text-center mb-2" style={{ color: 'var(--app-text-muted)' }}>
        We sent a verification link to
      </p>
      <p className="text-base font-medium text-center mb-8" style={{ color: 'var(--app-text)' }}>
        {authUser?.email ?? '…'}
      </p>
      <p className="text-sm text-center mb-10" style={{ color: 'var(--app-text-subtle)' }}>
        Click the link in the email to verify your account. This page will update automatically once verified.
      </p>

      <button
        onClick={handleResend}
        disabled={cooldown > 0 || sending}
        className="h-12 w-full rounded-xl text-base transition-opacity disabled:opacity-50"
        style={{
          backgroundColor: 'var(--app-card-bg)',
          border: '1px solid var(--app-border)',
          color: 'var(--app-text)',
        }}
      >
        {!canResend
          ? 'Sign in to resend verification email'
          : sending
          ? 'Sending…'
          : cooldown > 0
          ? `Resend in ${cooldown}s`
          : 'Resend verification email'}
      </button>

      <button
        onClick={handleSignOut}
        className="mt-4 text-sm active:opacity-70 transition-opacity"
        style={{ color: 'var(--app-text-subtle)' }}
      >
        Sign out
      </button>
    </div>
  );
}
